import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  MiniMap,
  getBezierPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomNode from './CustomNode';
import { v4 as uuidv4 } from 'uuid';
import CustomEdge from './CustomEdge';
import ChatbotAPI from './ChatbotAPI';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';

const nodeTypes = { custom: CustomNode };

const CONTENT_BLOCKS = [
  { type: 'text_button', label: 'Text Message', icon: '💬', description: 'Send a simple text message with quick reply buttons', category: 'basic' },
  { type: 'text_buttons', label: 'Text + Buttons', icon: '💬🔘', description: 'Send text message with action buttons', category: 'basic' },
  { type: 'media', label: 'Media Message', icon: '🖼️', description: 'Send image, video, or document with caption', category: 'media' },
  { type: 'template', label: 'Template Message', icon: '📋', description: 'Use pre-approved WhatsApp templates', category: 'template' },
  { type: 'condition', label: 'Condition', icon: '🔀', description: 'Add conditional logic to your flow', category: 'logic' },
  { type: 'user_input', label: 'Collect Input', icon: '📝', description: 'Collect user information and store as variables', category: 'logic' },
  { type: 'http_api', label: 'API Call', icon: '🔌', description: 'Make HTTP API calls and process responses', category: 'integration' },
  { type: 'delay', label: 'Delay', icon: '⏱️', description: 'Add delay between messages', category: 'basic' },
  { type: 'location', label: 'Location', icon: '📍', description: 'Send or request location information', category: 'media' },
  { type: 'list', label: 'List Message', icon: '📋', description: 'Send a list of options to choose from', category: 'interactive' },
];

function ConnectionMenu({ show, x, y, onSelect, onClose }) {
  if (!show) return null;

  const menuStyle = {
    position: 'absolute',
    left: x,
    top: y,
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    border: '1px solid #e1e5e9',
    zIndex: 1000,
    minWidth: '200px',
    maxHeight: '400px',
    overflowY: 'auto',
    transform: 'translate(-50%, -50%)'
  };

  const headerStyle = {
    padding: '12px 16px',
    borderBottom: '1px solid #e1e5e9',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '600',
    color: '#495057',
    fontSize: '14px'
  };

  const itemStyle = {
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#495057',
    borderBottom: '1px solid #f8f9fa',
    transition: 'background-color 0.2s ease'
  };

  const cancelButtonStyle = {
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#dc3545',
    borderTop: '1px solid #e1e5e9',
    transition: 'background-color 0.2s ease'
  };

  return (
    <div style={menuStyle}>
      <div style={headerStyle}>
        <span>☰</span>
        <span>Text</span>
      </div>
      
      {CONTENT_BLOCKS.map((block) => (
        <div
          key={block.type}
          style={itemStyle}
          onClick={() => onSelect(block.type)}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span style={{ fontSize: '16px' }}>{block.icon}</span>
          <span>{block.label}</span>
        </div>
      ))}
      
      <div
        style={cancelButtonStyle}
        onClick={onClose}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <span>✕</span>
        <span>Cancel</span>
      </div>
    </div>
  );
}

function FlowListModal({ open, onClose, onSelect, flows, onDelete }) {
  if (!open) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      zIndex: 10000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 12, 
        padding: 24, 
        minWidth: 500, 
        maxWidth: 600, 
        maxHeight: 600, 
        overflowY: 'auto', 
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)' 
      }}>
        <div style={{ 
          fontWeight: 600, 
          fontSize: 20, 
          marginBottom: 16, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          Load Flow
          <span style={{ cursor: 'pointer', fontSize: 24 }} onClick={onClose}>×</span>
        </div>
        
        {flows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            No flows found. Create your first flow!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {flows.map(flow => (
              <div key={flow.id} style={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 8, 
                padding: 16, 
                background: '#f9f9f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => onSelect(flow)}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                    {flow.name}
                  </div>
                  <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                    {flow.description || 'No description'}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Created: {new Date(flow.created_at).toLocaleDateString()}
                    {flow.is_active && <span style={{ color: '#25d366', marginLeft: 8 }}>● Active</span>}
                  </div>
                </div>
                <button 
                  onClick={() => onDelete(flow.id)}
                  style={{ 
                    background: '#ff4444', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 4, 
                    padding: '4px 8px', 
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        
        <button 
          onClick={onClose} 
          style={{ 
            marginTop: 16, 
            background: '#eee', 
            border: 'none', 
            borderRadius: 4, 
            padding: '8px 16px', 
            cursor: 'pointer', 
            width: '100%' 
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

function ContentBlockModal({ open, onSelect, onClose }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  if (!open) return null;
  
  const categories = [
    { id: 'all', label: 'All Blocks', icon: '🔧' },
    { id: 'basic', label: 'Basic', icon: '💬' },
    { id: 'media', label: 'Media', icon: '🖼️' },
    { id: 'interactive', label: 'Interactive', icon: '🎯' },
    { id: 'logic', label: 'Logic', icon: '🔀' },
    { id: 'integration', label: 'Integration', icon: '🔌' },
    { id: 'ai', label: 'AI', icon: '🤖' },
    { id: 'template', label: 'Templates', icon: '📋' }
  ];
  
  const filteredBlocks = selectedCategory === 'all' 
    ? CONTENT_BLOCKS 
    : CONTENT_BLOCKS.filter(block => block.category === selectedCategory);
  
  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      zIndex: 10000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 12, 
        padding: 24, 
        minWidth: 600, 
        maxWidth: 700, 
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)' 
      }}>
        <div style={{ 
          fontWeight: 600, 
          fontSize: 20, 
          marginBottom: 20, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          Add Content Block
          <span style={{ cursor: 'pointer', fontSize: 24, color: '#666' }} onClick={onClose}>×</span>
        </div>
        
        {/* Category Filter */}
        <div style={{ 
          display: 'flex', 
          gap: 8, 
          marginBottom: 20, 
          overflowX: 'auto',
          paddingBottom: 8
        }}>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 20,
                border: 'none',
                background: selectedCategory === category.id ? '#25d366' : '#f0f0f0',
                color: selectedCategory === category.id ? 'white' : '#333',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              <span>{category.icon}</span>
              <span>{category.label}</span>
            </button>
          ))}
        </div>
        
        {/* Content Blocks Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: 12, 
          overflowY: 'auto',
          flex: 1,
          paddingRight: 8
        }}>
          {filteredBlocks.map(block => (
            <div 
              key={block.type} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12, 
                padding: '16px', 
                borderRadius: 12, 
                cursor: 'pointer', 
                transition: 'all 0.2s',
                border: '2px solid #e0e0e0',
                background: '#fafafa'
              }}
              onClick={() => onSelect(block.type)}
              onMouseOver={e => {
                e.currentTarget.style.background = '#f0f7fa';
                e.currentTarget.style.borderColor = '#25d366';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.2)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = '#fafafa';
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: 28 }}>{block.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{block.label}</div>
                <div style={{ fontSize: 13, color: '#666', lineHeight: 1.4 }}>{block.description}</div>
              </div>
            </div>
          ))}
        </div>
        
        <button 
          onClick={onClose} 
          style={{ 
            marginTop: 20, 
            background: '#f0f0f0', 
            border: 'none', 
            borderRadius: 8, 
            padding: '12px 24px', 
            cursor: 'pointer', 
            width: '100%',
            fontSize: 16,
            fontWeight: 500,
            color: '#333'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default function EnhancedChatBuilder() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showContentBlockModal, setShowContentBlockModal] = useState(false);
  const [showFlowListModal, setShowFlowListModal] = useState(false);
  const [flows, setFlows] = useState([]);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [flowName, setFlowName] = useState('Untitled Flow');
  const [flowDescription, setFlowDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Connection menu state
  const [connectionMenu, setConnectionMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    sourceNode: null,
    sourceHandle: null
  });
  
  const reactFlowWrapper = useRef(null);

  // Load flows from backend
  const loadFlowsFromBackend = async () => {
    setIsLoading(true);
    try {
      const flowsData = await ChatbotAPI.loadFlows();
      setFlows(flowsData);
    } catch (error) {
      console.error('Error loading flows:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFlowsFromBackend();
  }, []);

  // Edge types with custom edge
  const edgeTypes = {
    custom: (props) => <CustomEdge {...props} setEdges={setEdges} />
  };

  // Add a new node with content block type
  const addNode = (contentType) => {
    const id = uuidv4();
    let nodeData = { message: '', buttons: [] };
    
    // Pre-fill node data based on content type
    switch(contentType) {
      case 'text_button':
        nodeData = {
          message: 'Hello! How can I help you today?',
          buttons: [
            { label: 'Get Started', target: null },
            { label: 'Learn More', target: null }
          ]
        };
        break;
      case 'text_buttons':
        nodeData = {
          message: '',
          buttons: []
        };
        break;
      case 'media':
        nodeData = {
          message: "Here's our latest product! Click below to learn more.",
          mediaUrl: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8bW9kZWx8ZW58MHx8MHx8fDA%3D',
          mediaType: 'image',
          buttons: [
            { label: 'Learn More', target: null },
            { label: 'Buy Now', target: null }
          ]
        };
        break;
      case 'template':
        nodeData = {
          templateId: null,
          templateName: 'Select Template',
          variables: {}
        };
        break;
      case 'condition':
        nodeData = {
          condition: 'contains:yes',
          trueMessage: 'Great! Let me help you with that.',
          falseMessage: 'I understand. Let me provide alternatives.',
          trueTarget: null,
          falseTarget: null
        };
        break;
      case 'user_input':
        nodeData = {
          prompt: 'Please enter your name:',
          variableName: 'customer_name',
          inputType: 'text',
          validation: {
            required: true,
            minLength: 2,
            errorMessage: 'Please enter a valid name (minimum 2 characters).'
          }
        };
        break;
      case 'http_api':
        nodeData = {
          url: 'https://api.example.com/data',
          method: 'GET',
          headers: {},
          body: '',
          responseVariable: 'api_response'
        };
        break;
      case 'delay':
        nodeData = {
          delay: 2,
          unit: 'seconds'
        };
        break;
      case 'location':
        nodeData = {
          message: 'Please share your location:',
          requestLocation: true,
          fallbackMessage: 'Location not available'
        };
        break;
      case 'list':
        nodeData = {
          message: 'Please choose an option:',
          listTitle: 'Select Option',
          listOptions: [
            { id: '1', title: 'Pricing', description: 'View our pricing plans' },
            { id: '2', title: 'Features', description: 'Learn about our features' },
            { id: '3', title: 'Contact Sales', description: 'Talk to our sales team' }
          ]
        };
        break;
      default:
        nodeData = {
          message: 'Hello! How can I help you today?',
          buttons: [
            { label: 'Get Started', target: null },
            { label: 'Learn More', target: null }
          ]
        };
    }

    const newNode = {
      id,
      type: 'custom',
      position: { x: 250, y: 100 + nodes.length * 150 },
      data: {
        ...nodeData,
        type: contentType,
        rootNodeId: nodes.length === 0 ? id : null,
        setNodeData: (updater) => {
          setNodes(nds => nds.map(node => 
            node.id === id 
              ? { ...node, data: typeof updater === 'function' ? updater(node.data) : updater }
              : node
          ));
        },
        onDelete: deleteNode,
        onCopy: (nodeId) => {
          const nodeToCopy = nodes.find(n => n.id === nodeId);
          if (nodeToCopy) {
            const newNodeId = uuidv4();
            const newNode = {
              ...nodeToCopy,
              id: newNodeId,
              position: { x: nodeToCopy.position.x + 200, y: nodeToCopy.position.y + 100 },
              data: {
                ...nodeToCopy.data,
                rootNodeId: null
              }
            };
            setNodes(nds => [...nds, newNode]);
          }
        }
      }
    };

    setNodes((nds) => [...nds, newNode]);
    setShowContentBlockModal(false);
  };

  // Delete a node
  const deleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  };

  // Handle node deletion
  const onNodesDelete = (nodesToDelete) => {
    nodesToDelete.forEach((node) => {
      deleteNode(node.id);
    });
  };

  // Handle edge connection
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Handle connection start (when user starts dragging from a handle)
  const onConnectStart = useCallback((event, { nodeId, handleId }) => {
    setConnectionMenu(prev => ({
      ...prev,
      sourceNode: nodeId,
      sourceHandle: handleId
    }));
  }, []);

  // Handle connection end (when user drops the connection)
  const onConnectEnd = useCallback((event) => {
    // Only show menu if we're not connecting to an existing node
    if (!event.target.closest('.react-flow__node')) {
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };
      
      setConnectionMenu(prev => ({
        ...prev,
        show: true,
        x: position.x,
        y: position.y
      }));
    } else {
      setConnectionMenu(prev => ({ ...prev, show: false }));
    }
  }, []);

  // Create node from connection menu
  const createNodeFromConnection = (contentType) => {
    if (!connectionMenu.sourceNode) return;

    const id = uuidv4();
    let nodeData = { message: '', buttons: [] };
    
    // Pre-fill node data based on content type
    switch(contentType) {
      case 'text_button':
        nodeData = {
          message: 'Hello! How can I help you today?',
          buttons: [
            { label: 'Get Started', target: null },
            { label: 'Learn More', target: null }
          ]
        };
        break;
      case 'text_buttons':
        nodeData = {
          message: '',
          buttons: []
        };
        break;
      case 'media':
        nodeData = {
          message: '',
          mediaUrl: '',
          mediaType: 'image',
          buttons: []
        };
        break;
      case 'list':
        nodeData = {
          message: 'Please choose an option:',
          listOptions: ['Pricing', 'Features', 'Contact Sales'],
          buttons: [
            { label: 'Back', target: null }
          ]
        };
        break;
      case 'single_product':
        nodeData = {
          message: 'Check out our featured product below!',
          product: { 
            name: 'SuperWidget 3000', 
            price: '$49.99', 
            image: 'https://via.placeholder.com/100x100.png?text=Widget',
            description: 'The best widget in the market!'
          },
          buttons: [
            { label: 'Buy Now', target: null },
            { label: 'More Info', target: null }
          ]
        };
        break;
      case 'condition':
        nodeData = {
          condition: 'contains:yes',
          trueMessage: 'Great! Let me help you with that.',
          falseMessage: 'I understand. Let me provide alternatives.'
        };
        break;
      case 'input':
        nodeData = {
          prompt: 'Please enter your name:',
          variableName: 'customer_name',
          validation: {
            required: true,
            minLength: 2,
            errorMessage: 'Please enter a valid name (minimum 2 characters).'
          }
        };
        break;
      case 'action':
        nodeData = {
          action: 'set_variable',
          variableName: 'customer_type',
          variableValue: 'premium'
        };
        break;
      case 'intervention':
        nodeData = {
          message: 'I\'m transferring you to a human agent. Please wait...',
          agentMessage: 'Customer needs human assistance'
        };
        break;
      default:
        nodeData = {
          message: 'Hello! How can I help you today?',
          buttons: [
            { label: 'Get Started', target: null },
            { label: 'Learn More', target: null }
          ]
        };
    }

    const newNode = {
      id,
      type: 'custom',
      position: { x: connectionMenu.x, y: connectionMenu.y },
      data: {
        ...nodeData,
        type: contentType,
        rootNodeId: null,
        setNodeData: (updater) => {
          setNodes(nds => nds.map(node => 
            node.id === id 
              ? { ...node, data: typeof updater === 'function' ? updater(node.data) : updater }
              : node
          ));
        },
        onDelete: deleteNode,
        onCopy: (nodeId) => {
          const nodeToCopy = nodes.find(n => n.id === nodeId);
          if (nodeToCopy) {
            const newNodeId = uuidv4();
            const newNode = {
              ...nodeToCopy,
              id: newNodeId,
              position: { x: nodeToCopy.position.x + 200, y: nodeToCopy.position.y + 100 },
              data: {
                ...nodeToCopy.data,
                rootNodeId: null
              }
            };
            setNodes(nds => [...nds, newNode]);
          }
        }
      }
    };

    // Add the new node
    setNodes((nds) => [...nds, newNode]);

    // Create the connection
    const newEdge = {
      id: `e${connectionMenu.sourceNode}-${id}`,
      source: connectionMenu.sourceNode,
      target: id,
      sourceHandle: connectionMenu.sourceHandle,
      targetHandle: null,
      type: 'custom'
    };

    setEdges((eds) => [...eds, newEdge]);

    // Hide the connection menu
    setConnectionMenu(prev => ({ ...prev, show: false }));
  };

  // Handle edge deletion
  const onEdgesDelete = (edgesToDelete) => {
    setEdges((eds) => eds.filter((edge) => !edgesToDelete.some((e) => e.id === edge.id)));
  };

  // Save flow to backend
  const saveFlowToBackend = async () => {
    if (nodes.length === 0) {
      ErrorToastContainer('Please add at least one node to save the flow');
      return;
    }

    setIsLoading(true);
    try {
      const flowData = {
        name: flowName,
        description: flowDescription,
        nodes: nodes,
        edges: edges,
        settings: {
          language: 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        is_active: true
      };

      let result;
      if (currentFlow) {
        result = await ChatbotAPI.updateFlow(currentFlow.id, flowData);
      } else {
        result = await ChatbotAPI.saveFlow(flowData);
      }

      if (result) {
        setCurrentFlow(result);
        SuccessToastContainer(currentFlow ? 'Flow updated successfully!' : 'Flow saved successfully!');
        loadFlowsFromBackend(); // Refresh flow list
      }
    } catch (error) {
      console.error('Error saving flow:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load flow from backend
  const loadFlowFromBackend = async (flow) => {
    setIsLoading(true);
    try {
      const flowData = await ChatbotAPI.getFlow(flow.id);
      if (flowData) {
        setNodes(flowData.nodes || []);
        setEdges(flowData.edges || []);
        setCurrentFlow(flowData);
        setFlowName(flowData.name || 'Untitled Flow');
        setFlowDescription(flowData.description || '');
        setShowFlowListModal(false);
        SuccessToastContainer('Flow loaded successfully!');
      }
    } catch (error) {
      console.error('Error loading flow:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete flow from backend
  const deleteFlowFromBackend = async (flowId) => {
    if (window.confirm('Are you sure you want to delete this flow? This action cannot be undone.')) {
      setIsLoading(true);
      try {
        const success = await ChatbotAPI.deleteFlow(flowId);
        if (success) {
          loadFlowsFromBackend(); // Refresh flow list
          if (currentFlow && currentFlow.id === flowId) {
            // Clear current flow if it was deleted
            setCurrentFlow(null);
            setNodes([]);
            setEdges([]);
            setFlowName('Untitled Flow');
            setFlowDescription('');
          }
        }
      } catch (error) {
        console.error('Error deleting flow:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Create new flow
  const createNewFlow = () => {
    setCurrentFlow(null);
    setFlowName('Untitled Flow');
    setFlowDescription('');
    
    // Create a default "Start Bot Flow" node
    const startNodeId = uuidv4();
    const startNode = {
      id: startNodeId,
      type: 'custom',
      position: { x: 250, y: 100 },
      data: {
        type: 'start',
        message: 'Start Bot Flow',
        keywords: ['Hello', 'Hi', 'Start'],
        matchingType: 'exact',
        title: 'Welcome Bot',
        addLabels: [],
        removeLabels: [],
        subscribeSequence: '',
        unsubscribeSequence: '',
        customField: '',
        assignToGroup: '',
        assignToUser: '',
        webhookUrl: '',
        rootNodeId: startNodeId,
        setNodeData: (updater) => {
          setNodes(nds => nds.map(node => 
            node.id === startNodeId 
              ? { ...node, data: typeof updater === 'function' ? updater(node.data) : updater }
              : node
          ));
        },
        onDelete: deleteNode,
        onCopy: (nodeId) => {
          const nodeToCopy = nodes.find(n => n.id === nodeId);
          if (nodeToCopy) {
            const newNodeId = uuidv4();
            const newNode = {
              ...nodeToCopy,
              id: newNodeId,
              position: { x: nodeToCopy.position.x + 200, y: nodeToCopy.position.y + 100 },
              data: {
                ...nodeToCopy.data,
                rootNodeId: null
              }
            };
            setNodes(nds => [...nds, newNode]);
          }
        }
      }
    };
    
    setNodes([startNode]);
    setEdges([]);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with flow controls */}
      <div style={{ 
        background: '#fff', 
        borderBottom: '1px solid #e0e0e0', 
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <input
            type="text"
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            style={{
              fontSize: 18,
              fontWeight: 600,
              border: '1px solid #ddd',
              borderRadius: 4,
              padding: '8px 12px',
              minWidth: 200
            }}
            placeholder="Flow name"
          />
          <input
            type="text"
            value={flowDescription}
            onChange={(e) => setFlowDescription(e.target.value)}
            style={{
              fontSize: 14,
              border: '1px solid #ddd',
              borderRadius: 4,
              padding: '8px 12px',
              minWidth: 300
            }}
            placeholder="Flow description"
          />
        </div>
        
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={createNewFlow}
            style={{
              padding: '8px 16px',
              background: '#f0f0f0',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            New Flow
          </button>
          <button
            onClick={() => setShowFlowListModal(true)}
            style={{
              padding: '8px 16px',
              background: '#128C7E',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            Load Flow
          </button>
          <button
            onClick={saveFlowToBackend}
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              background: '#25d366',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Saving...' : (currentFlow ? 'Update Flow' : 'Save Flow')}
          </button>
        </div>
      </div>

      {/* Flow builder area */}
      <div style={{ flex: 1, display: 'flex' }}>
        {/* Left sidebar */}
        <div style={{ 
          width: 280, 
          background: '#f8f9fa', 
          borderRight: '1px solid #e0e0e0',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}>
          <div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600 }}>Add Content</h3>
            <button
              onClick={() => setShowContentBlockModal(true)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#25d366',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              + Add Node
            </button>
          </div>

          <div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600 }}>Flow Info</h3>
            <div style={{ fontSize: 14, color: '#666' }}>
              <div>Nodes: {nodes.length}</div>
              <div>Connections: {edges.length}</div>
              {currentFlow && (
                <div style={{ marginTop: 8, padding: 8, background: '#e8f5e8', borderRadius: 4 }}>
                  <div style={{ color: '#25d366', fontWeight: 500 }}>● Flow Saved</div>
                  <div style={{ fontSize: 12 }}>ID: {currentFlow.id}</div>
                </div>
              )}
            </div>
          </div>

          {selectedNode && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600 }}>Selected Node</h3>
              <div style={{ fontSize: 14, color: '#666' }}>
                <div>Type: {selectedNode.data?.type}</div>
                <div>ID: {selectedNode.id}</div>
              </div>
            </div>
          )}
        </div>

        {/* Main flow area */}
        <div style={{ flex: 1, position: 'relative' }} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgesDelete}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-left"
          >
            <Background />
            <Controls />
            <MiniMap 
              style={{ background: '#f8f9fa' }}
              nodeColor="#25d366"
              nodeStrokeWidth={3}
            />
          </ReactFlow>
          
          {/* Connection Menu */}
          <ConnectionMenu
            show={connectionMenu.show}
            x={connectionMenu.x}
            y={connectionMenu.y}
            onSelect={createNodeFromConnection}
            onClose={() => setConnectionMenu(prev => ({ ...prev, show: false }))}
          />
        </div>
      </div>

      {/* Modals */}
      <ContentBlockModal
        open={showContentBlockModal}
        onSelect={addNode}
        onClose={() => setShowContentBlockModal(false)}
      />

      <FlowListModal
        open={showFlowListModal}
        onClose={() => setShowFlowListModal(false)}
        onSelect={loadFlowFromBackend}
        onDelete={deleteFlowFromBackend}
        flows={flows}
      />
    </div>
  );
} 